import React from 'react'
import gql from 'graphql-tag'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import { Query, Mutation } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import { excludeFrom, TagDialog } from '../../../legacy-design-system/tags'

export const availableTagsQuery = gql`
  query AvailableVulnerabilityTags($organizationID: String!) {
    organizationVulnerabilityTags(organizationID: $organizationID) {
      tags
    }
  }
`

interface ModifyTagProps {
  add: string[]
  remove: string[]
}

interface DialogProps {
  isOpen: boolean
  closeDialog: () => void
  initialTags: string[]
  organizationID: string
}

interface MutationProps {
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>
  vulnerabilityIDs: string[]
}

function ModifyVulnerabilityTags({
  isOpen,
  closeDialog,
  initialTags,
  organizationID,
  modifyTags,
  multipleVulnerabilities
}: DialogProps & {
  multipleVulnerabilities: boolean
  modifyTags: (props: ModifyTagProps) => Promise<any>
}) {
  return (
    <Query query={availableTagsQuery} variables={{ organizationID }}>
      {(result) => {
        const { data, error, loading, refetch } = result
        const availableTags =
          error || loading ? [] : data.organizationVulnerabilityTags.tags
        return (
          <ContrastFormattedMessage
            id={
              multipleVulnerabilities
                ? 'title_vulnerabilities_tagVulnerabilities'
                : 'title_tagVulnerability'
            }
          >
            {(title) => (
              <TagDialog
                initialAvailableTags={availableTags}
                initialSelectedTags={initialTags}
                onConfirm={(selectedTags) => {
                  const add = excludeFrom(selectedTags, initialTags)
                  const remove = excludeFrom(initialTags, selectedTags)

                  if (add.length + remove.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    modifyTags({ add, remove }).then(refetch)
                  }

                  closeDialog()
                }}
                onCancel={closeDialog}
                title={title}
                isOpen={isOpen}
              />
            )}
          </ContrastFormattedMessage>
        )
      }}
    </Query>
  )
}

const vulnerabilityTagsFragment = gql`
  fragment vulnerability on Vulnerability {
    tags
  }
`

function updateTags(
  vulnerabilityIDs: string[],
  add: string[],
  remove: string[]
) {
  return (proxy) => {
    vulnerabilityIDs.forEach((vulnerabilityID) => {
      const id =
        defaultDataIdFromObject({
          __typename: 'Vulnerability',
          id: vulnerabilityID
        }) || ''

      const { tags }: any = proxy.readFragment({
        id,
        fragment: vulnerabilityTagsFragment
      })

      proxy.writeFragment({
        id,
        fragment: vulnerabilityTagsFragment,
        data: {
          tags: tags.filter((t) => remove.every((r) => r !== t)).concat(add),
          __typename: 'Vulnerability'
        }
      })
    })
  }
}

function getOptimisticResponse(fieldName) {
  return {
    [fieldName]: {
      _placeholder: true,
      __typename: 'VulnerabilityBulkResponse'
    },
    __typename: 'Mutation'
  }
}

class ModifyTagsMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    ids: string[]
  }
> {}

const modifyVulnerabilitiesTagsMutationName = 'modifyVulnerabilitiesTags'
const modifyVulnerabilitiesTagsMutation = gql`
  mutation ${modifyVulnerabilitiesTagsMutationName}(
    $organizationID: ID!
    $add: [ID!]
    $remove: [ID!]
    $ids: [ID!]!
  ) {
    ${modifyVulnerabilitiesTagsMutationName}(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        ids: $ids
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyVulnerabilityTagsDialog({
  organizationID,
  vulnerabilityIDs,
  refetchQueries,
  refresh,
  initialTags,
  closeDialog,
  isOpen
}: DialogProps & MutationProps) {
  return (
    <ModifyTagsMutation
      mutation={modifyVulnerabilitiesTagsMutation}
      ignoreResults
    >
      {(mutate) => {
        return (
          <ModifyVulnerabilityTags
            isOpen={isOpen}
            initialTags={initialTags}
            closeDialog={closeDialog}
            multipleVulnerabilities={vulnerabilityIDs.length > 1}
            organizationID={organizationID}
            modifyTags={({ add, remove }) => {
              return mutate({
                refetchQueries,
                variables: {
                  add,
                  remove,
                  organizationID,
                  ids: vulnerabilityIDs
                },
                optimisticResponse: getOptimisticResponse(
                  modifyVulnerabilitiesTagsMutationName
                ),
                update: updateTags(vulnerabilityIDs, add, remove)
              }).then(refresh)
            }}
          />
        )
      }}
    </ModifyTagsMutation>
  )
}

class ModifyTagsByFilterMutation extends Mutation<
  any,
  ModifyTagProps & {
    organizationID: string
    excludedVulnerabilities: string[]
    filters: any
  }
> {}

const modifyVulnerabilityTagsByFilterMutationName =
  'modifyVulnerabilitiesTagsByFilters'
const modifyVulnerabilityTagsByFilterMutation = gql`
  mutation ${modifyVulnerabilityTagsByFilterMutationName}(
    $organizationID: ID!
    $add: [String!]!
    $remove: [String!]!
    $filters: VulnerabilityFilterArg!
    $excludedVulnerabilities: [String!]!
  ) {
    ${modifyVulnerabilityTagsByFilterMutationName}(
      input: {
        organizationID: $organizationID
        add: $add
        remove: $remove
        filters: $filters
        excludedVulnerabilities: $excludedVulnerabilities
      }
    ) {
      _placeholder
    }
  }
`

export function ModifyVulnerabilityTagsByFilterDialog({
  filters: { filterText, quickFilter, aggregations },
  additionalFilters,
  organizationID,
  excludedVulnerabilities,
  refresh,
  refetchQueries,
  initialTags,
  closeDialog,
  isOpen,
  vulnerabilityIDs
}: DialogProps &
  MutationProps & {
    organizationID: string
    excludedVulnerabilities: string[]
    filters: {
      filterText: string
      quickFilter: string
      aggregations: Record<string, string[]>
    }
    additionalFilters?: { [key: string]: any }
  }) {
  return (
    <ModifyTagsByFilterMutation
      mutation={modifyVulnerabilityTagsByFilterMutation}
    >
      {(mutate) => {
        return (
          <ModifyVulnerabilityTags
            initialTags={initialTags}
            closeDialog={closeDialog}
            isOpen={isOpen}
            multipleVulnerabilities={true}
            organizationID={organizationID}
            modifyTags={({ add, remove }) =>
              mutate({
                refetchQueries,
                variables: {
                  add,
                  remove,
                  filters: Object.assign({}, aggregations, {
                    filterText,
                    quickFilter,
                    ...additionalFilters
                  }),
                  organizationID,
                  excludedVulnerabilities
                },
                optimisticResponse: getOptimisticResponse(
                  modifyVulnerabilityTagsByFilterMutationName
                ),
                update: updateTags(vulnerabilityIDs, add, remove)
              }).then(refresh)
            }
          />
        )
      }}
    </ModifyTagsByFilterMutation>
  )
}
